import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CheckCircle, Close as CloseIcon, Add as AddIcon, Image as ImageIcon, FolderOutlined as FolderIcon } from "@mui/icons-material";
import DriveImagePicker from "./Drive/DriveImagePicker";
import NoticeboardButton from "./shared/NoticeboardButton";
import {
  FixedContainer,
  FixedHeader,
  FixedContent,
  FixedFooter,
} from "./CreationWizard/FormComponents";
import { getProfileImages, getSystemAssetThumbnail } from "../config/assets";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { lightTheme } from "../styles/theme";
import {
  fileToDataUrl,
  ORGANIZATION_LOGO_UPLOAD_OPTIONS,
  processImageForUpload,
  shouldProcessNoticeImage,
} from "../utils/imageUpload";

interface ProfileImageSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onImageSelect?: (file: File) => void | Promise<void>; // Required if returnBase64 is false
  onImageSelectBase64?: (base64: string) => void; // Required if returnBase64 is true
  predefinedImages?: string[];
  schoolId?: string;
  token?: string;
  returnBase64?: boolean; // If true, returns base64 string instead of File
  showSuccess?: boolean; // Show success message
}

// Helper function to convert image URL to File object
const urlToFile = async (url: string, filename: string): Promise<File> => {
  // no-store: logos are replaced at the same address, so a cached copy may be the old image.
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(`Image request failed (${response.status})`);
  const blob = await response.blob();
  if (!blob.type.startsWith("image/")) throw new Error(`Not an image: ${blob.type || "unknown type"}`);
  return new File([blob], filename, { type: blob.type });
};

const ProfileImageSelectorModal: React.FC<ProfileImageSelectorModalProps> = ({
  open,
  onClose,
  onImageSelect,
  onImageSelectBase64,
  predefinedImages,
  schoolId,
  token,
  returnBase64 = false,
  showSuccess = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const { showMessage } = useSnackbar();
  
  // Use provided predefinedImages or get from asset system based on org type
  const defaultPredefinedImages = predefinedImages || getProfileImages(user?.schoolType);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);
  // True only while a picked image is downloading; the picker can still be closed then.
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isDriveOpen, setIsDriveOpen] = useState(false);
  const driveButtonRef = useRef<HTMLButtonElement>(null);
  const canUseDrive = Boolean(user?.schoolId);
  // Each pick gets a number; a load only counts if it is still the latest and the picker is still open.
  const latestPick = useRef(0);
  useEffect(() => {
    latestPick.current += 1;
    setIsUploading(false);
    setIsLoadingImage(false);
    setSelectedImageIndex(null);
    setIsDriveOpen(false);
  }, [open]);

  const optimizeImageForBase64Selection = async (file: File): Promise<string> => {
    let fileToEncode = file;

    if (shouldProcessNoticeImage(file)) {
      try {
        fileToEncode = await processImageForUpload(
          file,
          ORGANIZATION_LOGO_UPLOAD_OPTIONS
        );
      } catch (error) {
        console.warn(
          `[ProfileImageSelectorModal] Failed to optimize "${file.name}", using original instead.`,
          error
        );
      }
    }

    return fileToDataUrl(fileToEncode);
  };

  const canClose = (!isUploading || isLoadingImage) && !showSuccess;
  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (returnBase64 && onImageSelectBase64) {
        setIsUploading(true);
        try {
          const base64 = await optimizeImageForBase64Selection(file);
          onImageSelectBase64(base64);
          onClose();
        } catch (error) {
          console.error("Error converting file to base64:", error);
        } finally {
          setIsUploading(false);
        }
      } else if (onImageSelect) {
        // Use File directly for profile uploads
        setIsUploading(true);
        try {
          await onImageSelect(file);
          onClose();
        } finally {
          setIsUploading(false);
        }
      }
    }
  };

  // Handle selection for both predefined and drive images
  const handleImageSelectClick = async (imageUrl: string, index: number) => {
    if (!onImageSelect && !(returnBase64 && onImageSelectBase64)) {
      console.warn("No image select handler provided");
      return;
    }
    const pick = ++latestPick.current;
    const isCurrent = () => pick === latestPick.current;
    setSelectedImageIndex(index);
    setIsUploading(true);
    setIsLoadingImage(true);
    try {
      const filename = imageUrl.split("/").pop()?.split("?")[0] || "image.png";
      const file = await urlToFile(imageUrl, filename);
      if (!isCurrent()) return;
      if (returnBase64 && onImageSelectBase64) {
        const base64 = await optimizeImageForBase64Selection(file);
        if (!isCurrent()) return;
        onImageSelectBase64(base64);
      } else if (onImageSelect) {
        setIsLoadingImage(false); // the screen is saving now, so stay locked until it finishes
        await onImageSelect(file);
      }
      if (isCurrent()) onClose();
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error processing image:", error);
      setSelectedImageIndex(null);
      showMessage({
        message: "Couldn't load that image. Try again, or upload a photo instead.",
        severity: "error",
      });
    } finally {
      if (isCurrent()) {
        setIsUploading(false);
        setIsLoadingImage(false);
      }
    }
  };
  const modalContent = (
    <>
      <IconButton
        onClick={handleClose}
        aria-label="Close"
        disabled={!canClose}
        sx={{
          position: "absolute",
          top: isMobile
            ? "calc(env(safe-area-inset-top, 0px) + 64px)"
            : 16,
          right: 16,
          zIndex: 10,
          backgroundColor: lightTheme.colors.whiteOverlay90,
          border: "none",
          borderRadius: "50%",
          padding: 1,
          minWidth: 32,
          minHeight: 32,
          boxShadow: `0 2px 8px ${lightTheme.colors.shadowBlack15}`,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.grey[100],
            transform: "scale(1.1)",
          },
        }}
      >
        <CloseIcon sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
      </IconButton>
      <FixedContainer>
        <FixedHeader>
          <Typography
            variant="h1"
            sx={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              marginTop: "1.2rem",
              marginBottom: "1.2rem",
              letterSpacing: "-0.2px",
              color: theme.palette.colors.teal,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            Select Image
          </Typography>
        </FixedHeader>

        <FixedContent>
          <Box
            sx={{
              width: "100%",
              maxWidth: "700px",
              padding: { xs: "1rem", sm: "2rem" },
              display: "flex",
              flexDirection: "column",
              gap: "2rem",
            }}
          >
        {/* Uploading State */}
        {isUploading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2" sx={{ color: "text.secondary" }} role="status">
              {isLoadingImage ? "Loading image…" : "Uploading..."}
            </Typography>
          </Box>
        )}

        {/* Success Message */}
        {showSuccess && !isUploading && (
          <Alert
            icon={<CheckCircle />}
            severity="success"
            sx={{
              borderRadius: 2,
              "& .MuiAlert-message": {
                fontWeight: 500,
              },
            }}
          >
            Logo uploaded successfully!
          </Alert>
        )}

            {/* Upload and Browse Options - Matching Advertisement Form */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                disabled={isUploading || showSuccess}
              />
              <Box
                component="button"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || showSuccess}
          sx={{
                  bgcolor: "transparent",
                  font: "inherit",
                  p: 0,
                  width: 120,
                  height: 120,
                  border: "2px dashed",
            borderColor: "divider",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isUploading || showSuccess ? "not-allowed" : "pointer",
                  gap: 0.5,
                  "&:hover": {
                    borderColor: isUploading || showSuccess ? "divider" : "primary.main",
                    bgcolor: isUploading || showSuccess ? "transparent" : "action.hover",
                  },
                  opacity: isUploading || showSuccess ? 0.6 : 1,
                }}
              >
                <AddIcon sx={{ color: "text.secondary", fontSize: 28 }} />
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                  Upload
                </Typography>
              </Box>
              {canUseDrive && (
                <Box
                  ref={driveButtonRef}
                  component="button"
                  type="button"
                  onClick={() => {
                    if (!isUploading && !showSuccess) setIsDriveOpen(true);
                  }}
                  disabled={isUploading || showSuccess}
                  sx={{
                    width: 120,
                    bgcolor: "transparent",
                    font: "inherit",
                    p: 0,
                    height: 120,
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isUploading || showSuccess ? "not-allowed" : "pointer",
                    gap: 0.5,
                    "&:hover": {
                      borderColor: isUploading || showSuccess ? "divider" : "primary.main",
                      bgcolor: isUploading || showSuccess ? "transparent" : "action.hover",
                    },
                    opacity: isUploading || showSuccess ? 0.6 : 1,
                  }}
                >
                  <FolderIcon sx={{ color: "text.secondary", fontSize: 28 }} />
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                    Drive
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Unified Image Gallery */}
            {defaultPredefinedImages.length > 0 ? (
              <>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: -1 }}>
                Default images
              </Typography>
              <Box
                id="image-gallery"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(3, 1fr)",
                      sm: "repeat(4, 1fr)",
                    md: "repeat(5, 1fr)",
                    },
                  gap: 1.5,
                  }}
                >
                {defaultPredefinedImages.map((imageUrl, index) => {
                    const globalIndex = index;
                    return (
                      <Box
                        key={`predefined-${index}`}
                        component="button"
                        type="button"
                        aria-label={`Default image ${index + 1}`}
                        disabled={isUploading || showSuccess}
                      onClick={() => {
                        if (!isUploading && !showSuccess) {
                          handleImageSelectClick(imageUrl, globalIndex);
                        }
                      }}
                        sx={{
                          position: "relative",
                          display: "block",
                          width: "100%",
                          p: 0,
                          font: "inherit",
                        cursor: isUploading || showSuccess ? "not-allowed" : "pointer",
                        borderRadius: 1.5,
                          overflow: "hidden",
                          aspectRatio: "1",
                        border: selectedImageIndex === globalIndex ? 2 : 0,
                        borderColor: "primary.main",
                        opacity: isUploading || showSuccess ? 0.6 : 1,
                        bgcolor: "grey.100",
                          "&:hover": {
                          border: isUploading || showSuccess ? 0 : 2,
                            borderColor: "primary.main",
                            "&::after": {
                            content: isUploading || showSuccess ? 'none' : '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                            backgroundColor: lightTheme.colors.shadowLight,
                            },
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        <img
                          src={getSystemAssetThumbnail(imageUrl)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          pointerEvents: "none",
                          }}
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.jpg";
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  textAlign: "center",
                }}
              >
                <ImageIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Upload an image, or choose one from Drive.
                </Typography>
              </Box>
            )}
          </Box>
        </FixedContent>

        <FixedFooter>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <NoticeboardButton
              variant="secondary"
              onClick={handleClose}
              disabled={!canClose}
            >
              Cancel
            </NoticeboardButton>
      </Box>
        </FixedFooter>
      </FixedContainer>
    </>
  );

  const dialogPaperSx = {
    borderRadius: isMobile ? 0 : 4,
    boxShadow: isMobile ? "none" : 24,
    p: 0,
    position: "relative",
    maxWidth: isMobile ? "100%" : 800,
    width: isMobile ? "100%" : "80%",
    height: isMobile ? "100vh" : "90vh",
    maxHeight: isMobile ? "100vh" : "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...(isMobile ? { margin: 0 } : {}),
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-label="Select Image Modal"
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      sx={{
        "& .MuiDialog-paper": dialogPaperSx,
        "& .MuiBackdrop-root": {
          backgroundColor: theme.palette.common.black + "59",
        },
      }}
    >
      {modalContent}
      {isDriveOpen && (
        <DriveImagePicker
          onClose={() => {
            setIsDriveOpen(false);
            // Put keyboard focus back where it was once the Drive dialog has gone.
            setTimeout(() => driveButtonRef.current?.focus());
          }}
          onSelect={(file) => {
            setIsDriveOpen(false);
            handleImageSelectClick(file.url, -1);
          }}
        />
      )}
    </Dialog>
  );
};

export default ProfileImageSelectorModal;
